<template>
  <component
    :is="tag"
    v-bind="boundProps"
    :class="computedClass"
    :disabled="isButton ? disabled : undefined"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { RouterLink } from 'vue-router';

type Variant = 'primary' | 'secondary';

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    to?: string | Record<string, any>;
    href?: string;
    type?: 'button' | 'submit' | 'reset';
    disabled?: boolean;
  }>(),
  {
    variant: 'primary',
    type: 'button',
    disabled: false,
  },
);

const attrs = useAttrs();
const forwardedAttrs = computed(() => {
  const { class: _class, ...rest } = attrs as Record<string, unknown>;
  return rest;
});

const isRouterLink = computed(() => props.to != null);
const isAnchor = computed(() => !isRouterLink.value && props.href != null);
const isButton = computed(() => !isRouterLink.value && !isAnchor.value);

const tag = computed(() => {
  if (isRouterLink.value) return RouterLink;
  if (isAnchor.value) return 'a';
  return 'button';
});

const boundProps = computed(() => {
  if (isRouterLink.value) {
    return {
      ...forwardedAttrs.value,
      to: props.to,
    };
  }

  if (isAnchor.value) {
    return {
      ...forwardedAttrs.value,
      href: props.href,
    };
  }

  return {
    ...forwardedAttrs.value,
    type: props.type,
  };
});

const baseClass = 'btn inline-flex items-center justify-center gap-2 backdrop-blur-sm';

const primaryClass = computed(() => {
  return 'bg-primary-dark text-white hover:bg-primary';
});

const secondaryClass = computed(() => {
  return 'border-2 border-primary-dark text-white hover:bg-primary';
});

const computedClass = computed(() => {
  const variantClass = props.variant === 'primary' ? primaryClass.value : secondaryClass.value;

  return [baseClass, variantClass, attrs.class].filter(Boolean);
});
</script>
